import { BRAND } from "constants/brand";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";

import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import useOrder from "hooks/useOrder";
import { useSelector } from "react-redux";
import CreatableSelect from "react-select/creatable";
import SelectField from "components/Ui/SelectField/SelectField";
import useArea from "hooks/useArea";
import { getVendorById } from "services/vendors";

/**
 * Keeps a name or a number in its own run inside an Arabic sentence. RLI, not
 * FSI: a name that starts with a Latin letter, such as «qa-سائق», then reads
 * as it does in the driver picker.
 */
const isolate = (text) => `\u2067${text}\u2069`;

/**
 * "طلب سريع" for staff — manual entry of an order that was placed OUTSIDE the
 * app (the customer phoned or WhatsApp-ed the vendor directly). Formerly
 * labelled "طلب واتس" and named AddWhatsAppOrder; relabelled so every manual
 * order-entry button in the product reads the same, and renamed so the three
 * sibling modals are each named for what they create.
 *
 * Opened by non-vendor admins from the /orders page. Posts to
 * POST api/order/dispatch with NO discriminator: the API resolves the policy
 * from the logged-in user, and role="admin" is the ONLY role that reaches this
 * one. It produces a type="whatsapp" order born status="finished" — immediately
 * claimable by drivers.
 *
 * «طلب خارجي» (the client, 2026-10-13) is a checkbox of this form, at the top.
 * The admin records a delivery that a driver ALREADY made outside the app: the
 * form asks for the driver and the delivery fee, and sends `deliveredBy` (the
 * driver's users.id). The order is then born `complete` for that driver, and
 * nobody gets an alarm, a push or a sound. Before it, the office sent a «طلب
 * سريع», another driver took it, and the office gave it back by hand. If the
 * driver's wallet is on, the API charges his share of the fee, also below ₪0:
 * the delivery happened. The rules and the refusals are the API's
 * (controllers/order.js#resolveDeliveredByDriver), not this form's.
 *
 * SHAPED LIKE THE اطلب اي غرض SHEET, BY REQUEST. The client, who is the only
 * person who files these, asked for the same inputs as the service-vendor form:
 * "دي نفس طلب سريع بتاع طلب اي غرض" (2026-07-26). الاسم and العنوان are gone;
 * what the driver needs goes in التفاصيل, exactly as it does there.
 *
 * TWO DIFFERENCES FROM THAT SHEET, both deliberate:
 *
 * 1. The shop is a CreatableSelect, not a text box — "المشرف يقدر يدخل اسم
 *    المطعم يدوي او من المطاعم الموجوده". Picking a real vendor files the order
 *    against them; typing a shop that is not in Talabatk sends no vendorId at
 *    all, and the API attaches the order to the city's dispatch account with the
 *    typed name as the first line of `notes` — the same composition the
 *    service-vendor sheet uses, so a driver cannot tell the two apart. For a
 *    «طلب خارجي» that is the dispatch account of the DRIVER's city.
 *
 * 2. المنطقه is still shown. The service sheet can omit it because a dispatch
 *    account has exactly one priced area to fall back to; a real restaurant has
 *    several, and "first priced area" would pick one arbitrarily — wrong fee,
 *    wrong drivers alarmed.
 *
 * THE MONEY FIELDS ARE CONDITIONAL, and the checkbox that reveals them is the
 * same one that sets `counts_in_vendor_statement`. That pairing is the point:
 * the statement bills Σ subtotal, so a counted order with no goods value would
 * appear on the vendor's statement contributing ₪0 — counted but not charged,
 * which reads as a billing failure. Ticked, the order is a real vendor SALE and
 * قيمه الطلب is required. Unticked, it is a dispatch job: no subtotal, and the
 * fee comes from the chosen area's configured cost. The API enforces the same
 * pairing (400 "أدخل قيمة الطلب لاحتسابه في كشف حساب المطعم") rather than
 * trusting this form to.
 *
 * قيمه التوصيل is optional even when billing: blank means "use the area's
 * cost". It used to be taken verbatim with no validation; the policy now shares
 * the vendor sheet's branch, so a negative or non-numeric fee is rejected. For
 * a «طلب خارجي» it is REQUIRED, and shown with the driver: the area's cost is
 * not what the customer paid him.
 *
 * The vendorId sent is the vendor's USER id, not the vendors-table id
 * (project-wide convention for order-creation endpoints).
 *
 * KNOWN VALIDATION GAPS (documented, not fixed):
 * - The vendor picker is a react-select, not a native form control, so the
 *   browser's `required` validation does not cover it. With neither a vendor
 *   nor a typed shop, no vendorId is sent, and the API files the order on the
 *   city's dispatch account, as for a typed shop with no name.
 * - المنطقه is uncontrolled and lists areas filtered by `vendor?.cityId`, so it
 *   is EMPTY until a vendor is picked, and changing the vendor afterwards swaps
 *   the option list underneath whatever the operator had already selected.
 *   Once the vendor's own areas load, the list narrows to those; it falls back
 *   to the city-wide list only if that fetch fails.
 * - `useArea()` fetches every area in the system on mount and filters client
 *   side; the list is not scoped to the operator's city.
 * - `phone` has no pattern check, and neither money field has an upper bound —
 *   a mistyped goods value flows straight into the vendor's commission. The fee
 *   of a «طلب خارجي» has one, in the API (₪500), because it moves the money of
 *   a driver.
 * - Nothing carries the delivery ADDRESS any more. That followed from matching
 *   the service sheet, where the driver phones the customer instead; if an
 *   operator does not put it in التفاصيل, the driver has only the phone number.
 * Error text here IS the API's Arabic message: `createQuickOrder` rethrows, and
 * all three modals now share that behaviour — the old asymmetry, where the
 * vendor form surfaced "Request failed with status code N" instead, is gone.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {() => void} props.close
 * @param {(type: string, head: string, content: string) => void} props.showMessage
 * @param {Array<{id: number, name: string, cityId: number}>} props.vendors
 * @param {Array<{value: number, label: string}>} props.vendorOptions
 * @param {Array<{id: number, name: string, cityId: number}>} props.drivers -
 *   the drivers that the orders page lists (`GET user/dropdown`), users.id
 * @param {Array<{id: number, name: string}>} props.cities
 */
const AddAdminQuickOrder = (props) => {
    const form = useRef();
    const nodeRef = useRef();
    // A second click before `loading` renders must not make a second order.
    const submitting = useRef(false);
    const { loading, createQuickOrder } = useOrder();
    const adminInfo = useSelector((state) => state.auth.userData);
    const [vendor, setVendor] = useState(null);
    // A shop the operator TYPED rather than picked, because it is not in
    // Talabatk. Mutually exclusive with `vendor`: whichever the last interaction
    // produced wins, so the two can never both be set and disagree.
    const [typedShop, setTypedShop] = useState("");
    // Drives BOTH the settlement flag and whether the money inputs are on screen.
    // One control for one decision: an order is either a real vendor sale, in
    // which case it needs a goods value, or it is a dispatch job, in which case
    // asking for one invites a number that would never be billed.
    const [countsInStatement, setCountsInStatement] = useState(false);
    // «طلب خارجي»: the driver already made the delivery.
    const [delivered, setDelivered] = useState(false);
    const [driverId, setDriverId] = useState(null);
    // Controlled, so that the button knows when the form can be sent.
    const [deliveredFee, setDeliveredFee] = useState("");
    const { areas } = useArea();
    // The vendor dropdown payload carries no areas (vendor/get-vendors-dropdown
    // returns id/name/cityId/vendorId only), so fetch the chosen vendor's own
    // delivery areas on demand. The API now rejects an area the vendor has no
    // delivery_cost for, so offering the whole city here would let an operator
    // pick an option that is guaranteed to 400.
    // Keyed by vendor id so the value is DERIVED below rather than reset in an
    // effect — switching vendors can never leave a previous vendor's list on
    // screen, and revisiting one is instant.
    const [areaIdsByVendor, setAreaIdsByVendor] = useState({});

    useEffect(() => {
        const vendorId = vendor?.id;
        if (!vendorId) {
            return undefined;
        }

        let cancelled = false;
        getVendorById(vendorId)
            .then((data) => {
                if (cancelled) {
                    return;
                }
                // vendor/get-vendor/:id responds { results: { ..., areas } }.
                const list = data?.results?.areas || [];
                setAreaIdsByVendor((prev) => ({ ...prev, [vendorId]: list.map((a) => a.id) }));
            })
            // Leave the entry unset so the city-wide list is used rather than
            // blocking the form; the API stays the real gate.
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [vendor?.id]);

    const drivers = props.drivers || [];
    const driver = delivered ? drivers.find((d) => +d.id === +driverId) || null : null;
    const cityName = (cityId) => props.cities?.find((c) => +c.id === +cityId)?.name;
    // A super admin can see the drivers of each city, and two drivers can have
    // one name.
    const driverOptions = drivers.map((d) => ({
        value: d.id,
        label:
            adminInfo?.super_admin && cityName(d.cityId)
                ? `${d.name} — ${cityName(d.cityId)}`
                : d.name,
    }));
    const deliveredFeeIsValid = Number(deliveredFee) > 0;

    // The API refuses a restaurant of another city than the driver's, so do
    // not offer one.
    const vendorOptions = driver
        ? (props.vendorOptions || []).filter(
              (o) => +props.vendors?.find((v) => +v.id === +o.value)?.cityId === +driver.cityId,
          )
        : props.vendorOptions;

    const vendorAreaIds = vendor?.id ? areaIdsByVendor[vendor.id] : null;
    // Three cases, and the third is why this is not just `vendor?.cityId`:
    //   picked a vendor, areas loaded  -> exactly that vendor's priced areas
    //   picked a vendor, still loading -> that vendor's city, as a wide default
    //   TYPED a shop (no vendor)       -> the ADMIN's own city
    // The typed case matters because the API attaches such an order to the
    // caller's city dispatch account, so the area must belong to that same city
    // or the order is filed under an area its vendor cannot serve. Without this
    // the list came out empty and the form could not be submitted at all.
    // A «طلب خارجي» with no vendor goes on the dispatch account of the
    // DRIVER's city, so its areas come from that city.
    const fallbackCityId = vendor?.cityId ?? driver?.cityId ?? adminInfo?.cityId;
    const selectableAreas = areas.filter((a) =>
        vendorAreaIds ? vendorAreaIds.includes(a.id) : a.cityId === fallbackCityId,
    );

    // The state lives above the CSSTransition, so it outlives a close, while
    // the restaurant picker remounts empty. Without this reset the form opened
    // with no restaurant on screen and still sent the last one, and a «طلب
    // خارجي» would be recorded for the last driver.
    const closeForm = () => {
        setVendor(null);
        setTypedShop("");
        setCountsInStatement(false);
        setDelivered(false);
        setDriverId(null);
        setDeliveredFee("");
        props.close();
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        if (submitting.current || (delivered && !(driver && deliveredFeeIsValid))) {
            return;
        }
        const orderData = new FormData();

        // Named fields, read through FormData. The previous version indexed
        // form.current[1..7] by DOM POSITION, so inserting or reordering a single
        // Form.Group silently shifted every field after it — name became phone,
        // total became shipping — with no type or name to catch it. The vendor
        // picker is a react-select and is not a form control, so its value is
        // appended separately.
        const raw = new FormData(form.current);

        // Either a real vendor, or a shop that is not in Talabatk. With a vendor
        // the order is filed against them exactly as before; with a typed name
        // no vendorId is sent at all and the API attaches the order to the
        // city's dispatch account instead, putting the name where the driver
        // reads it — the first line of `notes`, matching how the service-vendor
        // sheet composes the same field.
        if (vendor) {
            orderData.append("vendorId", vendor.id);
        }
        // The driver picker is a react-select too. The fee is the `shipping`
        // field of the form.
        if (driver) {
            orderData.append("deliveredBy", driver.id);
        }

        for (const [key, value] of raw.entries()) {
            if (key === "notes" && !vendor && typedShop) {
                const extra = String(value).trim();
                orderData.append("notes", extra ? `${typedShop}\n${extra}` : typedShop);
            } else {
                orderData.append(key, value);
            }
        }

        // A typed shop with an empty notes box still has to reach the driver.
        if (!vendor && typedShop && !raw.has("notes")) {
            orderData.append("notes", typedShop);
        }

        submitting.current = true;
        try {
            const created = await createQuickOrder(orderData);
            if (driver) {
                // From the answer, not from the form: the API decides whether
                // the wallet of the driver pays.
                const debit = Number(created?.walletDebit?.fee);
                props.showMessage(
                    "success",
                    "تم تسجيل الطلب",
                    `الطلب ${isolate(`#${created?.order?.id}`)} مكتمل للسائق ${isolate(driver.name)}.` +
                        (debit > 0 ? ` خُصمت رسوم الطلب (${isolate(debit)} شيكل) من محفظته.` : ""),
                );
            } else {
                props.showMessage("success", "تمت الاضافه", "تمت إضافه الطلب بنجاح");
            }
            closeForm();
        } catch (err) {
            props.showMessage(
                "error",
                "هناك خطأ",
                err.response?.data?.message || "حدث خطأ غير متوقع",
            );
        } finally {
            submitting.current = false;
        }
    };

    return (
        <>
            <div className={`model ${props.show ? "model-show" : ""}`} onClick={closeForm}>
                <CSSTransition
                    mountOnEnter
                    unmountOnExit
                    nodeRef={nodeRef}
                    in={props.show}
                    timeout={400}
                    classNames="show"
                >
                    <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="loading-spinner center">
                                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                            </div>
                        ) : null}
                        <div className="head center">
                            <h5>طلب سريع</h5>
                            <FontAwesomeIcon icon={faClose} onClick={closeForm} />
                        </div>
                        <Form className="form" ref={form} onSubmit={submitHandler}>
                            {/* At the top: it changes which fields the form asks
                                for. It has no `name`, so FormData does not carry
                                it; the API reads `deliveredBy`. */}
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    id="deliveredOutsideApp"
                                    checked={delivered}
                                    onChange={(e) => setDelivered(e.target.checked)}
                                    label="طلب خارجي: السائق وصّل الطلب"
                                />
                                <Form.Text muted>
                                    يُسجَّل الطلب مكتملًا للسائق الذي تختاره، ولا يصل إشعار لأي أحد.
                                </Form.Text>
                            </Form.Group>
                            {delivered ? (
                                <>
                                    <Form.Group className="mb-3" controlId="deliveredBy">
                                        <Form.Label>
                                            السائق<span>*</span>
                                        </Form.Label>
                                        <SelectField
                                            inputId="deliveredBy"
                                            options={driverOptions}
                                            value={
                                                driverOptions.find((o) => +o.value === +driverId) ||
                                                null
                                            }
                                            placeholder="اختر السائق"
                                            noOptionsMessage={() => "لا يوجد سائق"}
                                            isSearchable
                                            isClearable
                                            onChange={(selected) =>
                                                setDriverId(selected ? selected.value : null)
                                            }
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="deliveredFee">
                                        <Form.Label>
                                            قيمه التوصيل (₪)<span>*</span>
                                        </Form.Label>
                                        <Form.Control
                                            name="shipping"
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            value={deliveredFee}
                                            onChange={(e) => setDeliveredFee(e.target.value)}
                                            required
                                        />
                                        <Form.Text muted>
                                            المبلغ الذي أخذه السائق للتوصيل. إذا كانت محفظته مفعّلة،
                                            تُخصم منها رسوم الطلب، ولو صار الرصيد أقل من صفر.
                                        </Form.Text>
                                    </Form.Group>
                                </>
                            ) : null}
                            <Form.Group className="mb-3" controlId="0">
                                <Form.Label>
                                    {delivered ? (
                                        "المطعم (اختياري)"
                                    ) : (
                                        <>
                                            المطعم<span>*</span>
                                        </>
                                    )}
                                </Form.Label>
                                <CreatableSelect
                                    options={vendorOptions}
                                    classNamePrefix="rs"
                                    placeholder="اختر المطعم أو اكتب اسمه"
                                    formatCreateLabel={(input) => `استخدم "${input}"`}
                                    isSearchable
                                    isClearable
                                    onChange={(selected) => {
                                        if (!selected) {
                                            setVendor(null);
                                            setTypedShop("");
                                            return;
                                        }
                                        // __isNew__ is react-select/creatable's
                                        // marker for a value the user typed
                                        // rather than picked.
                                        if (selected.__isNew__) {
                                            setVendor(null);
                                            setTypedShop(selected.value);
                                            return;
                                        }
                                        setTypedShop("");
                                        setVendor(
                                            props.vendors.find((v) => +v.id === +selected.value),
                                        );
                                    }}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="1">
                                <Form.Label>التفاصيل (اختياري)</Form.Label>
                                <Form.Control name="notes" as="textarea" rows={3} placeholder="" />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="2">
                                <Form.Label>
                                    {delivered ? (
                                        "رقم الهاتف (اختياري)"
                                    ) : (
                                        <>
                                            رقم الهاتف<span>*</span>
                                        </>
                                    )}
                                </Form.Label>
                                {/* A delivered order calls no driver, so nobody
                                    needs the number. The API never required it. */}
                                <Form.Control
                                    name="phone"
                                    type="text"
                                    placeholder=""
                                    required={!delivered}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="status">
                                <Form.Label>
                                    المنطقه<span>*</span>
                                </Form.Label>
                                <Form.Select name="areaId">
                                    {selectableAreas?.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    id="countsInVendorStatement"
                                    name="countsInVendorStatement"
                                    value="true"
                                    checked={countsInStatement}
                                    onChange={(e) => setCountsInStatement(e.target.checked)}
                                    label="احتساب هذا الطلب في كشف حساب المطعم"
                                />
                                <Form.Text muted>
                                    غير مفعّل افتراضيًا. فعّله فقط إذا كانت طلبية حقيقية للمطعم
                                    تُحاسب عليها — طلبات التوصيل العادية لا تُحتسب.
                                </Form.Text>
                            </Form.Group>
                            {/* The money inputs exist only for a billable order.
                                Unticked, they are UNMOUNTED rather than hidden, so
                                FormData carries neither: the API then reads
                                subtotal 0 and takes the fee from the area's
                                configured cost, which is what makes the default
                                shape identical to the اطلب اي غرض sheet. A
                                «طلب خارجي» has its fee above, with the driver, so
                                the second fee field is not shown. */}
                            {countsInStatement ? (
                                <>
                                    <Form.Group className="mb-3" controlId="4">
                                        <Form.Label>
                                            قيمه الطلب (₪) <span>*</span>
                                        </Form.Label>
                                        <Form.Control
                                            name="total"
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            placeholder=""
                                            required
                                        />
                                        <Form.Text muted>
                                            قيمة البضاعة — هي أساس عمولة المطعم في كشف الحساب.
                                        </Form.Text>
                                    </Form.Group>
                                    {delivered ? null : (
                                        <Form.Group className="mb-3" controlId="5">
                                            <Form.Label>قيمه التوصيل (₪، اختياري)</Form.Label>
                                            <Form.Control
                                                name="shipping"
                                                type="number"
                                                min={0}
                                                step="0.01"
                                                placeholder=""
                                            />
                                            <Form.Text muted>
                                                اتركه فارغاً لاستخدام أجرة المنطقة المحددة.
                                            </Form.Text>
                                        </Form.Group>
                                    )}
                                </>
                            ) : null}
                            <div className="buttons">
                                <button className="cancel" onClick={closeForm} type="button">
                                    إلغاء
                                </button>
                                <button
                                    className="button"
                                    type="submit"
                                    disabled={
                                        loading || (delivered && !(driver && deliveredFeeIsValid))
                                    }
                                >
                                    {delivered ? "تسجيل الطلب" : "إرسال"}
                                </button>
                            </div>
                        </Form>
                    </div>
                </CSSTransition>
            </div>
        </>
    );
};
export default AddAdminQuickOrder;
